import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Truck,
  Search,
  Star,
  MapPin,
  Clock,
  Phone,
  CheckCircle2,
  Shield,
  Zap,
  Package,
  ArrowRight,
} from 'lucide-react';
import axios from 'axios';
import { DeliveryTracker } from '../components/services/DeliveryIntegration';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeliveryServicesPage = () => {
  const { formatCurrency } = useCountry();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [trackingId, setTrackingId] = useState('');
  const [showTracker, setShowTracker] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/delivery/providers?country=SL`);
      setProviders(response.data.providers || []);
    } catch (err) {
      console.error('Failed to load delivery providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProviders = providers.filter(p => 
    p.provider_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleTrackOrder = () => {
    if (trackingId.trim()) {
      setShowTracker(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4 flex items-center gap-3">
              <Truck className="h-10 w-10" />
              Delivery Services
            </h1>
            <p className="text-xl text-green-100 mb-6">
              Fast, reliable pickup and delivery partners for your laundry, dry cleaning, and more
            </p>
            
            {/* Track Order */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <h3 className="font-medium mb-2">Track Your Delivery</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter your order ID (e.g., DEL-XXXXXXXX)"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  className="bg-white text-gray-900"
                />
                <Button 
                  onClick={handleTrackOrder}
                  className="bg-white text-green-600 hover:bg-green-50"
                >
                  Track
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Tracking Result */}
        {showTracker && trackingId && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Tracking: {trackingId}</h2>
              <Button variant="outline" onClick={() => setShowTracker(false)}>
                Close Tracker
              </Button>
            </div>
            <DeliveryTracker orderId={trackingId} />
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search delivery services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Badge variant="outline" className="text-sm">
            {filteredProviders.length} providers available
          </Badge>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Zap className="h-8 w-8 text-green-600" />
              <div>
                <p className="font-medium">Express Delivery</p>
                <p className="text-sm text-gray-600">Same-day available</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className="h-8 w-8 text-blue-600" />
              <div>
                <p className="font-medium">Real-time Tracking</p>
                <p className="text-sm text-gray-600">Track your order live</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Shield className="h-8 w-8 text-purple-600" />
              <div>
                <p className="font-medium">Insured Delivery</p>
                <p className="text-sm text-gray-600">Your items are protected</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Package className="h-8 w-8 text-orange-600" />
              <div>
                <p className="font-medium">All Sizes</p>
                <p className="text-sm text-gray-600">Bike, car, or van</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Providers List */}
        <h2 className="text-2xl font-bold mb-4">Available Delivery Partners</h2>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading delivery services...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredProviders.map((provider) => (
              <Card 
                key={provider.id} 
                className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${
                  selectedProvider?.id === provider.id ? 'ring-2 ring-green-500' : ''
                }`}
                onClick={() => setSelectedProvider(provider)}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold">{provider.provider_name}</h3>
                        {provider.is_third_party && (
                          <Badge variant="outline" className="text-xs">3rd Party API</Badge>
                        )}
                      </div>
                      {provider.is_sponsored && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          Recommended
                        </Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                        <span className="font-bold">{provider.rating?.toFixed(1)}</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {provider.review_count?.toLocaleString()} reviews
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4">{provider.description}</p>

                  {/* Specializations */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {provider.specializations?.slice(0, 4).map((spec, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {spec}
                      </Badge>
                    ))}
                  </div>

                  {/* Coverage Areas */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <MapPin className="h-4 w-4" />
                    <span>Coverage: {provider.coverage_areas?.slice(0, 3).join(', ')}</span>
                  </div>

                  {/* Pricing */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium mb-2">Pricing</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {provider.fixed_price_services?.slice(0, 4).map((service, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="text-gray-600">{service.name}</span>
                          <span className="font-medium">{formatCurrency(service.price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Operating Hours */}
                  {provider.operating_hours && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <Clock className="h-4 w-4" />
                      <span>
                        {provider.operating_hours.start} - {provider.operating_hours.end}
                      </span>
                    </div>
                  )}

                  {/* Achievements */}
                  <div className="flex items-center gap-2 mb-4">
                    {provider.achievements?.includes('verified_business') && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                    {provider.achievements?.includes('insured_service') && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">
                        <Shield className="h-3 w-3 mr-1" />
                        Insured
                      </Badge>
                    )}
                    {provider.achievements?.includes('fast_delivery') && (
                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                        <Zap className="h-3 w-3 mr-1" />
                        Fast
                      </Badge>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-green-600 hover:bg-green-700">
                      Book Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <Phone className="h-4 w-4 mr-2" />
                      Contact
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* How It Works */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6 text-center">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">1</span>
              </div>
              <h3 className="font-medium mb-2">Book a Service</h3>
              <p className="text-sm text-gray-600">
                Choose laundry or dry cleaning service and schedule pickup
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="font-medium mb-2">We Pick Up</h3>
              <p className="text-sm text-gray-600">
                Our delivery partner collects your items from your doorstep
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="font-medium mb-2">Professional Cleaning</h3>
              <p className="text-sm text-gray-600">
                Your items are cleaned by expert professionals
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">4</span>
              </div>
              <h3 className="font-medium mb-2">We Deliver</h3>
              <p className="text-sm text-gray-600">
                Clean items delivered back to you, fresh and ready
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryServicesPage;
